import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { toApiLocalTimestamp, toLocalInput } from "@/lib/fichajeUi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function diaDeTimestamp(ts) {
  if (!ts) return null;
  if (ts.length >= 10 && ts[4] === "-") return ts.slice(0, 10);
  const d = new Date(ts.replace(/ /g, "+"));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("sv");
}

function splitFechaHora(ts) {
  const v = (ts || "").trim();
  if (v.length >= 16) return { fecha: v.slice(0, 10), hora: v.slice(11, 16) };
  if (v.length >= 10 && v[4] === "-") return { fecha: v.slice(0, 10), hora: "" };
  return { fecha: "", hora: "" };
}

function joinFechaHora(fecha, hora) {
  const f = (fecha || "").trim();
  const h = (hora || "").trim();
  if (!f || !h) return "";
  return `${f}T${h}`;
}

function solicitudCubrePicado(solicitudes, p) {
  return solicitudes.some((s) => {
    if (p.es_modificacion && p.fichaje_id && s.es_modificacion && s.fichaje_id === p.fichaje_id) {
      return true;
    }
    if (s.es_modificacion) return false;
    if (s.tipo_fichaje !== p.tipo_fichaje) return false;
    const franja = p.franja || "";
    if (franja && (s.franja === franja || s.motivo?.includes(`tramo ${franja}`))) return true;
    if (p.timestamp_sugerido && s.timestamp) {
      const diff = Math.abs(new Date(p.timestamp_sugerido).getTime() - new Date(s.timestamp).getTime());
      return diff < 5 * 60 * 1000;
    }
    return false;
  });
}

function tsKeyPicado(p) {
  return `${p.tramo}-${p.tipo_fichaje}`;
}

function tipoInicial(estado, solicitudPendienteTipo, preferido) {
  if (preferido && !solicitudPendienteTipo(preferido)) return preferido;
  const puedeEntrada = !solicitudPendienteTipo("entrada");
  const puedeSalida = !solicitudPendienteTipo("salida");
  if (estado?.dentro && puedeSalida) return "salida";
  if (!puedeEntrada && puedeSalida) return "salida";
  if (!puedeSalida && puedeEntrada) return "entrada";
  return preferido || "entrada";
}

/**
 * @param {"sheet"|"dialog"|"inline"} presentacion
 *   sheet  = panel inferior (móvil / Fichar)
 *   dialog = modal centrado
 *   inline = formulario embebido en la página
 */
export default function SolicitudPicadoManualDialog({
  estado: estadoProp,
  open,
  onOpenChange,
  onSuccess,
  trigger,
  defaultTipo,
  defaultTimestamp,
  defaultDia,
  testId = "manual-dialog",
  presentacion = "sheet",
}) {
  const { user } = useAuth();
  const esAdminSinSupervision = user?.rol === "admin";
  const [manual, setManual] = useState({ tipo: "entrada", timestamp: "", justificacion: "" });
  const [pendienteEdit, setPendienteEdit] = useState({});
  const [enviandoPendiente, setEnviandoPendiente] = useState(null);
  const [enviandoManual, setEnviandoManual] = useState(false);
  const [estadoDia, setEstadoDia] = useState(null);
  const [cargandoEstado, setCargandoEstado] = useState(false);

  const diaActivo = useMemo(() => {
    const desdeForm = diaDeTimestamp(manual.timestamp);
    if (desdeForm) return desdeForm;
    if (defaultDia) return defaultDia;
    if (defaultTimestamp) return diaDeTimestamp(defaultTimestamp);
    return hoyISO();
  }, [manual.timestamp, defaultDia, defaultTimestamp]);

  useEffect(() => {
    if (!open) {
      setEstadoDia(null);
      return;
    }
    let cancel = false;
    setCargandoEstado(true);
    api
      .get("/fichajes/estado", { params: { dia: diaActivo } })
      .then(({ data }) => {
        if (!cancel) setEstadoDia(data);
      })
      .catch(() => {
        if (!cancel) setEstadoDia(estadoProp?.dia === diaActivo ? estadoProp : null);
      })
      .finally(() => {
        if (!cancel) setCargandoEstado(false);
      });
    return () => {
      cancel = true;
    };
  }, [open, diaActivo, estadoProp]);

  const estado = estadoDia || (estadoProp?.dia === diaActivo ? estadoProp : null);

  const picadosPendientes = useMemo(() => estado?.picados_pendientes || [], [estado]);
  const solicitudesPendientes = useMemo(() => estado?.solicitudes_fichaje_pendientes || [], [estado]);
  const franjasDia = estado?.horario_jornada?.franjas || [];
  const esHoy = diaActivo === hoyISO();
  const etiquetaDia = esHoy ? "hoy" : fmtFecha(`${diaActivo}T12:00:00`);

  const solicitudPendienteTipo = useCallback(
    (tipo) => solicitudesPendientes.some((s) => !s.es_modificacion && s.tipo_fichaje === tipo),
    [solicitudesPendientes]
  );

  const picadoBloqueado = useCallback(
    (p) => !p.es_modificacion && solicitudCubrePicado(solicitudesPendientes, p),
    [solicitudesPendientes]
  );

  const picadosEnviables = useMemo(
    () => picadosPendientes.filter((p) => !picadoBloqueado(p)),
    [picadosPendientes, picadoBloqueado]
  );

  const recargarEstado = useCallback(async () => {
    const { data } = await api.get("/fichajes/estado", { params: { dia: diaActivo } });
    setEstadoDia(data);
    return data;
  }, [diaActivo]);

  const puedeEntrada = !solicitudPendienteTipo("entrada");
  const puedeSalida = !solicitudPendienteTipo("salida");
  const puedeEnviarLibre = puedeEntrada || puedeSalida;
  const tipoBloqueado = solicitudPendienteTipo(manual.tipo);

  const cerrar = useCallback(() => {
    onOpenChange?.(false);
    setManual({ tipo: "entrada", timestamp: "", justificacion: "" });
    setPendienteEdit({});
    setEnviandoManual(false);
    setEnviandoPendiente(null);
    setEstadoDia(null);
  }, [onOpenChange]);

  const onDialogOpenChange = (next) => {
    if (next) {
      onOpenChange?.(true);
      return;
    }
    cerrar();
  };

  useEffect(() => {
    if (!open) return;
    let timestamp = "";
    if (defaultTimestamp) {
      const raw = typeof defaultTimestamp === "string" ? defaultTimestamp.replace(/ /g, "+") : defaultTimestamp;
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        timestamp = toLocalInput(d.toISOString());
      } else if (typeof defaultTimestamp === "string" && defaultTimestamp.length >= 16) {
        timestamp = defaultTimestamp.slice(0, 16);
      }
    } else {
      timestamp = defaultDia || hoyISO();
    }
    setManual({ tipo: defaultTipo || "entrada", timestamp, justificacion: "" });
    setPendienteEdit({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir
  }, [open]);

  useEffect(() => {
    if (!open || !estado || cargandoEstado) return;
    setManual((prev) => {
      const tipo = tipoInicial(estado, solicitudPendienteTipo, defaultTipo || prev.tipo);
      if (tipo === prev.tipo) return prev;
      return { ...prev, tipo };
    });
  }, [open, estado, cargandoEstado, defaultTipo, solicitudPendienteTipo]);

  useEffect(() => {
    if (!open || !picadosPendientes.length) return;
    const diaBase = defaultDia || diaActivo || hoyISO();
    setPendienteEdit((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const p of picadosPendientes) {
        const key = tsKeyPicado(p);
        if (!(key in next)) {
          next[key] = diaBase;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, picadosPendientes, defaultDia, diaActivo]);

  const onCambioFecha = (timestamp) => {
    setManual((prev) => {
      const nuevoDia = diaDeTimestamp(timestamp);
      const diaAnterior = diaDeTimestamp(prev.timestamp);
      let tipo = prev.tipo;
      if (nuevoDia && nuevoDia !== diaAnterior) {
        tipo = defaultTipo && nuevoDia === defaultDia ? defaultTipo : prev.tipo;
      }
      return { ...prev, timestamp, tipo };
    });
  };

  const ficharManual = async () => {
    if (enviandoManual) return;
    const fh = splitFechaHora(manual.timestamp);
    if (!fh.fecha || !fh.hora) {
      toast.error("Indica la fecha y hora del picado");
      return;
    }
    if (manual.justificacion.trim().length < 3) {
      toast.error("Indica una justificación para el fichaje manual");
      return;
    }
    if (tipoBloqueado) {
      toast.error(`Ya tienes una solicitud de ${manual.tipo} pendiente de aprobación para este día`);
      return;
    }
    let tsApi;
    try {
      tsApi = toApiLocalTimestamp(joinFechaHora(fh.fecha, fh.hora));
    } catch (err) {
      toast.error(err.message || "Indica fecha y hora del picado");
      return;
    }
    setEnviandoManual(true);
    try {
      const { data } = await api.post("/solicitudes", {
        tipo: "fichaje_manual",
        tipo_fichaje: manual.tipo,
        timestamp_solicitado: tsApi,
        motivo: manual.justificacion.trim(),
      });
      cerrar();
      toast.success(
        data?.estado === "aprobada"
          ? "Picado registrado"
          : "Solicitud enviada a tu encargado para aprobación"
      );
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviandoManual(false);
    }
  };

  const solicitarPendiente = async (p) => {
    if (enviandoPendiente || enviandoManual) return;
    const justificacion = manual.justificacion.trim();
    if (justificacion.length < 3) {
      toast.error("Indica una justificación común para los picados manuales");
      return;
    }
    if (picadoBloqueado(p)) {
      toast.error(`Ya tienes una solicitud de ${p.tipo_fichaje} pendiente para este tramo`);
      return;
    }
    const tsKey = tsKeyPicado(p);
    const fh = splitFechaHora(pendienteEdit[tsKey]);
    if (!fh.fecha || !fh.hora) {
      toast.error("Indica la fecha y hora del picado");
      return;
    }
    let tsApi;
    try {
      tsApi = toApiLocalTimestamp(joinFechaHora(fh.fecha, fh.hora));
    } catch (err) {
      toast.error(err.message || "Indica la hora del picado");
      return;
    }
    setEnviandoPendiente(tsKey);
    try {
      if (p.es_modificacion && p.fichaje_id) {
        const { data } = await api.post("/fichajes/solicitar-correccion", {
          fichaje_id: p.fichaje_id,
          timestamp: tsApi,
          motivo: justificacion,
        });
        toast.success(
          data?.estado === "aprobada"
            ? `Corrección del tramo ${p.franja} aplicada`
            : `Corrección del tramo ${p.franja} enviada al encargado`
        );
      } else {
        const { data } = await api.post("/solicitudes", {
          tipo: "fichaje_manual",
          tipo_fichaje: p.tipo_fichaje,
          timestamp_solicitado: tsApi,
          motivo: `${justificacion} (tramo ${p.franja})`,
        });
        toast.success(
          data?.estado === "aprobada"
            ? `Picado del tramo ${p.franja} registrado`
            : `Picado manual del tramo ${p.franja} enviado al encargado`
        );
      }
      const data = await recargarEstado();
      const quedan = (data?.picados_pendientes || []).filter((x) => !solicitudCubrePicado(data?.solicitudes_fichaje_pendientes || [], x));
      if (quedan.length === 0) {
        cerrar();
        onSuccess?.();
      } else {
        onSuccess?.();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviandoPendiente(null);
    }
  };

  const solicitarTodosPendientes = async () => {
    if (enviandoManual || enviandoPendiente) return;
    const justificacion = manual.justificacion.trim();
    if (justificacion.length < 3) {
      toast.error("Indica una justificación común para los picados manuales");
      return;
    }
    if (!picadosEnviables.length) {
      toast.error("No hay picados pendientes por enviar");
      return;
    }
    const sinHora = picadosEnviables.filter((p) => {
      const fh = splitFechaHora(pendienteEdit[tsKeyPicado(p)]);
      return !fh.fecha || !fh.hora;
    });
    if (sinHora.length) {
      toast.error("Indica la fecha y hora de cada picado pendiente");
      return;
    }
    setEnviandoManual(true);
    let enviados = 0;
    try {
      for (const p of picadosEnviables) {
        const tsKey = tsKeyPicado(p);
        const fh = splitFechaHora(pendienteEdit[tsKey]);
        const tsApi = toApiLocalTimestamp(joinFechaHora(fh.fecha, fh.hora));
        if (p.es_modificacion && p.fichaje_id) {
          await api.post("/fichajes/solicitar-correccion", {
            fichaje_id: p.fichaje_id,
            timestamp: tsApi,
            motivo: justificacion,
          });
        } else {
          await api.post("/solicitudes", {
            tipo: "fichaje_manual",
            tipo_fichaje: p.tipo_fichaje,
            timestamp_solicitado: tsApi,
            motivo: `${justificacion} (tramo ${p.franja})`,
          });
        }
        enviados += 1;
      }
      toast.success(
        esAdminSinSupervision
          ? enviados === 1
            ? "1 picado registrado"
            : `${enviados} picados registrados`
          : enviados === 1
            ? "1 solicitud enviada a tu encargado"
            : `${enviados} solicitudes enviadas a tu encargado`
      );
      cerrar();
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
      if (enviados > 0) {
        await recargarEstado();
        onSuccess?.();
      }
    } finally {
      setEnviandoManual(false);
    }
  };

  const manualFH = splitFechaHora(manual.timestamp);
  const manualCompleto = Boolean(manualFH.fecha && manualFH.hora);

  const setManualFecha = (fecha) => {
    if (manualFH.hora) onCambioFecha(joinFechaHora(fecha, manualFH.hora));
    else onCambioFecha(fecha || "");
  };
  const setManualHora = (hora) => {
    const fecha = manualFH.fecha || defaultDia || hoyISO();
    onCambioFecha(joinFechaHora(fecha, hora));
  };

  const setPendienteFecha = (tsKey, fecha) => {
    const cur = splitFechaHora(pendienteEdit[tsKey]);
    setPendienteEdit((prev) => ({
      ...prev,
      [tsKey]: cur.hora ? joinFechaHora(fecha, cur.hora) : (fecha || ""),
    }));
  };
  const setPendienteHora = (tsKey, hora) => {
    const cur = splitFechaHora(pendienteEdit[tsKey]);
    const fecha = cur.fecha || defaultDia || hoyISO();
    setPendienteEdit((prev) => ({ ...prev, [tsKey]: joinFechaHora(fecha, hora) }));
  };

  const pendienteCompleto = (tsKey) => {
    const fh = splitFechaHora(pendienteEdit[tsKey]);
    return Boolean(fh.fecha && fh.hora);
  };

  const camposFechaHora = ({ fecha, hora, onFecha, onHora, disabled, testIdPrefix }) => (
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1">
        <Label className="text-xs">
          Fecha <span className="text-rose-600">*</span>
        </Label>
        <Input
          type="date"
          required
          disabled={disabled}
          value={fecha || ""}
          onChange={(e) => onFecha(e.target.value)}
          data-testid={`${testIdPrefix}-fecha`}
          className="bg-white"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">
          Hora <span className="text-rose-600">*</span>
        </Label>
        <Input
          type="time"
          required
          step={60}
          disabled={disabled}
          value={hora || ""}
          onChange={(e) => onHora(e.target.value)}
          data-testid={`${testIdPrefix}-hora`}
          className="bg-white text-base"
        />
      </div>
    </div>
  );

  const cuerpo = (
    <div className="space-y-4" data-testid={testId}>
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
        Elige <strong>fecha</strong> y <strong>hora</strong> del picado. No se usa la hora actual automáticamente.
      </p>

      {cargandoEstado && (
        <p className="rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-500">Cargando datos del {etiquetaDia}…</p>
      )}

      {!cargandoEstado && puedeEnviarLibre && (
        <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
            {picadosPendientes.length > 0 ? "Añadir otro picado" : "Nuevo picado"}
          </p>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={manual.tipo} onValueChange={(v) => setManual((prev) => ({ ...prev, tipo: v }))}>
              <SelectTrigger data-testid="manual-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" className="z-[220]">
                <SelectItem value="entrada" disabled={!puedeEntrada}>
                  Entrada{puedeEntrada ? "" : " (ya enviada)"}
                </SelectItem>
                <SelectItem value="salida" disabled={!puedeSalida}>
                  Salida{puedeSalida ? "" : " (ya enviada)"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {camposFechaHora({
            fecha: manualFH.fecha,
            hora: manualFH.hora,
            onFecha: setManualFecha,
            onHora: setManualHora,
            testIdPrefix: "manual",
          })}
          {!manualCompleto && (
            <p className="text-xs text-rose-600">Obligatorio: indica fecha y hora.</p>
          )}
          {picadosPendientes.length === 0 && (
            <div className="space-y-1.5">
              <Label>Justificación (obligatoria)</Label>
              <Textarea
                placeholder="Ej.: olvidé fichar al salir"
                value={manual.justificacion}
                onChange={(e) => setManual((prev) => ({ ...prev, justificacion: e.target.value }))}
              />
            </div>
          )}
          {picadosPendientes.length > 0 && (
            <p className="text-xs text-zinc-500">
              Usa la justificación de la sección de abajo. Pulsa «Enviar solicitud» para este picado.
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {presentacion === "inline" && (
              <Button type="button" variant="outline" onClick={cerrar}>
                Cancelar
              </Button>
            )}
            <Button
              type="button"
              onClick={ficharManual}
              disabled={
                enviandoManual
                || tipoBloqueado
                || cargandoEstado
                || Boolean(enviandoPendiente)
                || !manualCompleto
              }
              data-testid="manual-submit-btn"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {enviandoManual ? "Enviando…" : "Enviar solicitud"}
            </Button>
          </div>
        </div>
      )}

      {!cargandoEstado && franjasDia.length > 0 && (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-xs text-indigo-900">
          <p className="font-semibold uppercase tracking-wide text-indigo-700/80">
            Horario {esHoy ? "de hoy" : `del ${etiquetaDia}`}
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {franjasDia.map((f, i) => (
              <Badge key={i} variant="outline" className="border-indigo-200 bg-white text-indigo-800">
                Tramo {i + 1}: {f.inicio}–{f.fin}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {!cargandoEstado && picadosPendientes.length === 0 && defaultDia && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
          data-testid="manual-dia-corregido"
        >
          Este día no tiene picados pendientes: el aviso ya está corregido o el registro está completo.
        </div>
      )}

      {!cargandoEstado && picadosPendientes.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Picados pendientes por tramo</p>
          <p className="text-xs text-amber-800/80">
            Debes indicar la fecha y hora de cada picado (no se rellenan solas).
          </p>
          <div className="space-y-1.5">
            <Label>Justificación (común para todos)</Label>
            <Textarea
              placeholder="Ej.: olvidé fichar al salir del primer tramo"
              value={manual.justificacion}
              onChange={(e) => setManual((prev) => ({ ...prev, justificacion: e.target.value }))}
              rows={2}
            />
          </div>
          {picadosPendientes.map((p) => {
            const tsKey = tsKeyPicado(p);
            const bloqueado = picadoBloqueado(p);
            const fh = splitFechaHora(pendienteEdit[tsKey]);
            return (
              <div
                key={tsKey}
                className={`rounded-lg border p-3 text-sm ${
                  bloqueado ? "border-zinc-200 bg-zinc-50 opacity-70" : "border-amber-200 bg-amber-50/60"
                }`}
              >
                <p className="font-medium text-amber-950">
                  Tramo {p.tramo} ({p.franja}) — {p.tipo_fichaje === "entrada" ? "Entrada" : "Salida"}
                  {p.es_modificacion && " (corregir automático)"}
                  {bloqueado && " · en revisión"}
                </p>
                <p className="mt-0.5 text-xs text-amber-800/70">{p.detalle}</p>
                {p.timestamp_sugerido && (
                  <p className="mt-1 text-xs text-amber-700/80">
                    Sugerida: {fmtFecha(p.timestamp_sugerido)} {toLocalInput(p.timestamp_sugerido).slice(11, 16)}
                  </p>
                )}
                <div className="mt-2">
                  {camposFechaHora({
                    fecha: fh.fecha,
                    hora: fh.hora,
                    onFecha: (v) => setPendienteFecha(tsKey, v),
                    onHora: (v) => setPendienteHora(tsKey, v),
                    disabled: bloqueado,
                    testIdPrefix: `manual-pendiente-${tsKey}`,
                  })}
                  {!pendienteCompleto(tsKey) && !bloqueado && (
                    <p className="mt-1 text-xs text-rose-600">Obligatorio: indica día y hora.</p>
                  )}
                </div>
                {!bloqueado && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full border-amber-400 text-amber-900 hover:bg-amber-100"
                    disabled={
                      enviandoPendiente === tsKey
                      || enviandoManual
                      || !pendienteCompleto(tsKey)
                    }
                    onClick={() => solicitarPendiente(p)}
                  >
                    {enviandoPendiente === tsKey ? "Enviando…" : "Solicitar solo este"}
                  </Button>
                )}
              </div>
            );
          })}
          {picadosEnviables.length > 1 && (
            <Button
              type="button"
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={enviandoManual || Boolean(enviandoPendiente)}
              onClick={solicitarTodosPendientes}
              data-testid="manual-enviar-todos-btn"
            >
              {enviandoManual ? "Enviando…" : `Enviar todos (${picadosEnviables.length} picados)`}
            </Button>
          )}
        </div>
      )}

      {!cargandoEstado && solicitudesPendientes.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2 text-xs text-sky-950">
          <p className="font-semibold">En revisión por el encargado ({etiquetaDia})</p>
          <ul className="mt-1 space-y-0.5">
            {solicitudesPendientes.map((s) => (
              <li key={s.solicitud_id}>
                {s.tipo_fichaje === "entrada" ? "Entrada" : "Salida"} a las {s.hora}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!cargandoEstado && !puedeEnviarLibre && picadosPendientes.length === 0 && (
        <p className="rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
          Ya tienes solicitudes de entrada y salida en revisión para este día. Usa el botón Salir en Fichar para
          registrar la salida en tiempo real, o espera la aprobación del encargado.
        </p>
      )}
    </div>
  );

  if (presentacion === "inline") {
    if (!open) return null;
    return (
      <div className="w-full max-w-sm space-y-3 rounded-2xl border border-amber-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-lg font-medium text-zinc-900">Picado manual</h3>
          <Button type="button" variant="ghost" size="sm" onClick={cerrar}>
            Cerrar
          </Button>
        </div>
        {cuerpo}
      </div>
    );
  }

  if (presentacion === "dialog") {
    return (
      <Dialog open={!!open} onOpenChange={onDialogOpenChange}>
        {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
        <DialogContent className="z-[100] max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Picado manual</DialogTitle>
          </DialogHeader>
          {cuerpo}
          <DialogFooter className="sr-only" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={!!open} onOpenChange={onDialogOpenChange}>
      <SheetContent
        side="bottom"
        className="z-[100] mx-auto max-h-[92vh] max-w-lg overflow-y-auto rounded-t-2xl"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display">Picado manual</SheetTitle>
        </SheetHeader>
        <div className="mt-4 pb-6">{cuerpo}</div>
        <SheetFooter className="sr-only" />
      </SheetContent>
    </Sheet>
  );
}
